import React, { useEffect, useMemo, useState } from "react";
import "./Run_history.css";

// FastAPI base URL.
// In production, /api_backend is forwarded to FastAPI by Nginx.

const API_URL = process.env.REACT_APP_API_FAST;
const REFRESH_INTERVAL_MS = 3000;

// Normalize status values for comparisons and CSS classes.
const statusOf = (value) =>
    String(value || "unknown").toLowerCase();

function formatStatus(status) {
    const labels = {
        success: "Success",
        completed: "Completed",
        failed: "Failed",
        error: "Error",
        running: "Running",
        pending: "Pending",
        skipped: "Skipped",
        cancelled: "Cancelled",
        canceled: "Cancelled",
    };

    const key = statusOf(status);
    return labels[key] || status || "Unknown";
}

function StatusBadge({ status }) {
    const key = statusOf(status);

    return (
        <span className={`run-status status-${key}`}>
            <span className="status-dot" />
            {formatStatus(status)}
        </span>
    );
}

function displayTime(value) {
    if (!value) return null;

    const date = new Date(value);

    return Number.isNaN(date.getTime())
        ? String(value)
        : date.toLocaleString();
}

function durationFor(item) {
    if (item.duration !== null && item.duration !== undefined) {
        const seconds = Number(item.duration);

        if (!Number.isFinite(seconds)) {
            return String(item.duration);
        }

        if (seconds < 60) {
            return `${Math.floor(seconds)} sec`;
        }

        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);

        return remainingSeconds
            ? `${minutes} min ${remainingSeconds} sec`
            : `${minutes} min`;
    }

    if (!item.started_at) return null;

    const start = new Date(item.started_at).getTime();
    const end = item.finished_at
        ? new Date(item.finished_at).getTime()
        : Date.now();

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return null;
    }

    const seconds = Math.max(0, (end - start) / 1000);

    if (seconds < 60) {
        return `${Math.floor(seconds)} sec`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return remainingSeconds
        ? `${minutes} min ${remainingSeconds} sec`
        : `${minutes} min`;
}

// Display the children belonging to one main task.
function ChildTimeline({ children = [] }) {
    const ordered = [...children].sort(
        (a, b) =>
            Number(a.order_number ?? a.priority ?? 0) -
            Number(b.order_number ?? b.priority ?? 0)
    );

    if (!ordered.length) {
        return (
            <div className="no-children">
                <span>No child tasks recorded.</span>
            </div>
        );
    }

    return (
        <div className="child-timeline">
            {ordered.map((child, index) => {
                const status = statusOf(child.status);

                return (
                    <React.Fragment
                        key={child.id ?? `${child.name}-${index}`}
                    >
                        <div className={`child-step child-${status}`}>
                            <div className="child-step-indicator">
                                {["success", "completed"].includes(status)
                                    ? "✓"
                                    : ["failed", "error"].includes(status)
                                    ? "!"
                                    : status === "running"
                                    ? "↻"
                                    : status === "skipped"
                                    ? "–"
                                    : "○"}
                            </div>

                            <div className="child-step-content">
                                <div className="child-step-heading">
                                    <span className="child-priority">
                                        {String(
                                            child.order_number ??
                                                child.priority ??
                                                index + 1
                                        ).padStart(2, "0")}
                                    </span>

                                    <span className="child-name">
                                        {child.name || child.task_name || "Unnamed child"}
                                    </span>

                                    <StatusBadge status={child.status} />
                                </div>

                                <div className="child-step-meta">
                                    {durationFor(child) && (
                                        <span>{durationFor(child)}</span>
                                    )}

                                    {child.started_at && (
                                        <span>
                                            Started {displayTime(child.started_at)}
                                        </span>
                                    )}

                                    {child.finished_at && (
                                        <span>
                                            Finished {displayTime(child.finished_at)}
                                        </span>
                                    )}
                                </div>

                                {(child.error || child.error_message) && (
                                    <div className="child-error">
                                        <strong>Failure reason</strong>
                                        <p>
                                            {child.error || child.error_message}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {index < ordered.length - 1 && (
                            <div
                                className={`child-connector ${
                                    ["success", "completed"].includes(status)
                                        ? "connector-success"
                                        : ""
                                }`}
                                aria-hidden="true"
                            >
                                <span>↓</span>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

// Display one main task and its child tasks.
function MainTask({ task }) {
    const status = statusOf(task.status);

    const [expanded, setExpanded] = useState(
        ["failed", "error"].includes(status)
    );

    return (
        <div className={`main-task main-${status}`}>
            <button
                type="button"
                className="main-task-header"
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
            >
                <span className="main-task-icon">
                    {["success", "completed"].includes(status)
                        ? "✓"
                        : ["failed", "error"].includes(status)
                        ? "!"
                        : status === "running"
                        ? "↻"
                        : "○"}
                </span>

                <span className="main-task-info">
                    <span className="main-task-name">
                        {task.name || task.task_name || "Unnamed main task"}
                    </span>

                    <span className="main-task-subtitle">
                        {task.priority != null
                            ? `Priority ${task.priority}`
                            : "Main task"}

                        {task.started_at &&
                            ` · Started ${displayTime(task.started_at)}`}

                        {durationFor(task) &&
                            ` · ${durationFor(task)}`}
                    </span>
                </span>

                <StatusBadge status={task.status} />

                <span
                    className={`expand-chevron ${
                        expanded ? "open" : ""
                    }`}
                >
                    ⌄
                </span>
            </button>

            {expanded && (
                <div className="main-task-details">
                    {task.finished_at && (
                        <div className="task-finish-info">
                            Finished at {displayTime(task.finished_at)}
                        </div>
                    )}

                    {(task.error || task.error_message) && (
                        <div className="child-error">
                            <strong>Failure reason</strong>
                            <p>{task.error || task.error_message}</p>
                        </div>
                    )}

                    <div className="children-heading">
                        <span>CHILD EXECUTION FLOW</span>
                        <span>
                            {task.children?.length || 0} children
                        </span>
                    </div>

                    <ChildTimeline children={task.children || []} />
                </div>
            )}
        </div>
    );
}

// Display one workflow run, containing its main tasks.
function RunCard({ run }) {
    const status = statusOf(run.status);

    const [expanded, setExpanded] = useState(
        ["failed", "error"].includes(status)
    );

    const tasks = [...(run.tasks || [])].sort(
        (a, b) =>
            Number(a.order_number ?? a.priority ?? 0) -
            Number(b.order_number ?? b.priority ?? 0)
    );

    const children = tasks.flatMap((task) => task.children || []);

    const passed = children.filter((task) =>
        ["success", "completed"].includes(statusOf(task.status))
    ).length;

    const failed = children.filter((task) =>
        ["failed", "error"].includes(statusOf(task.status))
    ).length;

    return (
        <article className={`run-card run-card-${status}`}>
            <button
                type="button"
                className="run-card-header"
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
            >
                <div className="run-card-title-area">
                    <div className="run-card-icon">↗</div>

                    <div>
                        <div className="run-id-label">
                            WORKFLOW RUN
                        </div>

                        <h3 className="run-id">{run.run_id}</h3>

                        <div className="run-time">
                            <span>◷</span>{" "}
                            {displayTime(run.started_at) ||
                                "Start time unavailable"}
                        </div>
                    </div>
                </div>

                <div className="run-card-summary">
                    <div className="run-summary-count">
                        <strong>{tasks.length}</strong>
                        <span>Main tasks</span>
                    </div>

                    <div className="run-summary-count">
                        <strong>{passed}</strong>
                        <span>Children passed</span>
                    </div>

                    <div className="run-summary-count">
                        <strong>{failed}</strong>
                        <span>Children failed</span>
                    </div>

                    <StatusBadge status={run.status} />

                    <span
                        className={`expand-chevron ${
                            expanded ? "open" : ""
                        }`}
                    >
                        ⌄
                    </span>
                </div>
            </button>

            {expanded && (
                <div className="run-card-body">
                    <div className="run-overview">
                        <div>
                            <span className="overview-label">Started</span>
                            <strong>
                                {displayTime(run.started_at) || "—"}
                            </strong>
                        </div>

                        <div>
                            <span className="overview-label">Finished</span>
                            <strong>
                                {displayTime(run.finished_at) ||
                                    "Still running"}
                            </strong>
                        </div>

                        <div>
                            <span className="overview-label">Main tasks</span>
                            <strong>{tasks.length}</strong>
                        </div>
                    </div>

                    {run.error_message && (
                        <div className="child-error">
                            <strong>Run failure</strong>
                            <p>{run.error_message}</p>
                        </div>
                    )}

                    <div className="task-list-heading">
                        <h4>Execution history</h4>
                        <span>Ordered by execution order</span>
                    </div>

                    <div className="main-task-list">
                        {tasks.map((task, index) => (
                            <React.Fragment
                                key={task.id ?? `${task.name}-${index}`}
                            >
                                <MainTask task={task} />

                                {index < tasks.length - 1 && (
                                    <div className="main-task-connector">
                                        <span>↓ Next main task</span>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}
        </article>
    );
}

// Main Run History component.
export function Run_history() {
    // Show all runs by default.
    const [filter, setFilter] = useState("all");
    const [runs, setRuns] = useState([]);
    console.log("runs_is",runs)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        let active = true;

        async function loadRuns(initial = false) {
            if (initial && active) {
                setLoading(true);
            }

            try {
                const response = await fetch(
                    `${API_URL}/workflow-runs?limit=50`,
                    {
                        headers: {
                            Accept: "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `Request failed (${response.status})`
                    );
                }

                const payload = await response.json();

                if (!active) return;

                const fetchedRuns = Array.isArray(payload)
                    ? payload
                    : payload.runs || [];

                setRuns(fetchedRuns);
                setError("");
                setLastUpdated(new Date());
            } catch (err) {
                if (active) {
                    setError(
                        err.message ||
                            "Could not load workflow runs."
                    );
                }
            } finally {
                if (initial && active) {
                    setLoading(false);
                }
            }
        }

        // Fetch immediately when the component first loads.
        loadRuns(true);

        // Fetch again every three seconds to update task statuses.
        const timer = window.setInterval(() => {
            loadRuns(false);
        }, REFRESH_INTERVAL_MS);

        // Stop polling when the component is removed.
        return () => {
            active = false;
            window.clearInterval(timer);
        };
    }, []);

    // Apply the selected status filter.
    const filteredRuns = useMemo(
        () =>
            runs.filter(
                (run) =>
                    filter === "all" ||
                    statusOf(run.status) === filter
            ),
        [runs, filter]
    );

    return (
        <section className="run-history">
            <header className="history-header">
                <div>
                    <div className="history-eyebrow">
                        WORKFLOW MONITOR
                    </div>

                    <h2>Run History</h2>

                    <p>
                        Track workflow runs, inspect task execution,
                        and find exactly where a workflow failed.
                    </p>
                </div>

                <div className="history-total">
                    <span>Total runs</span>
                    <strong>{runs.length}</strong>
                </div>
            </header>

            <div className="history-toolbar">
                <div>
                    <h3>Workflow executions</h3>

                    <span>
                        {filteredRuns.length} runs displayed
                    </span>

                    {lastUpdated && (
                        <span>
                            {" "}· Updated{" "}
                            {lastUpdated.toLocaleTimeString()}
                        </span>
                    )}
                </div>

                <div className="history-filters">
                    {[
                        ["all", "All runs"],
                        ["success", "Successful"],
                        ["completed", "Completed"],
                        ["failed", "Failed"],
                        ["running", "Running"],
                        ["pending", "Pending"],
                    ].map(([value, label]) => (
                        <button
                            key={value}
                            type="button"
                            className={
                                filter === value
                                    ? "filter-active"
                                    : ""
                            }
                            onClick={() => setFilter(value)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {error && (
                <div className="history-empty" role="alert">
                    <h3>Unable to refresh run history</h3>
                    <p>{error}</p>
                    <p>
                        Check that the FastAPI endpoint is reachable.
                    </p>
                </div>
            )}

            {loading ? (
                <div className="history-empty">
                    <span>◷</span>
                    <h3>Loading workflow runs…</h3>
                </div>
            ) : (
                <div className="run-card-list">
                    {filteredRuns.length > 0 ? (
                        filteredRuns.map((run) => (
                            <RunCard
                                key={run.run_id}
                                run={run}
                            />
                        ))
                    ) : (
                        <div className="history-empty">
                            <span>◷</span>

                            <h3>
                                {runs.length > 0
                                    ? "No matching runs"
                                    : "No workflow runs yet"}
                            </h3>

                            <p>
                                {runs.length > 0
                                    ? "Try a different status filter."
                                    : "Runs will appear here after a workflow has been recorded."}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

export default Run_history;